import { NotImplementedError } from "../../../../errors";
import { OVERVIEW_SCREENSHOT_FIELDS, OVERVIEW_SCREENSHOTS_QUERY_LIMIT, parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { SCREENSHOTS_QUERY } from "./query";
/**
 * Typed handler for the Overview dashboard Screenshots widget. Mirrors
 * LookerOverviewPlacements, including its fail-closed partner scoping.
 */
export default class LookerOverviewScreenshots extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-screenshots");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const queryBody = {
            ...SCREENSHOTS_QUERY,
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter)
            }
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        if (mediaTypeFilter?.value) {
            queryBody.filters["explore_overview.product_group"] = mediaTypeFilter.value;
        }
        // Fail closed: without advertiser/campaign ids the query would return
        // screenshots across every partner. Rejects missing ids only -- it does not
        // check the caller owns them, the same gap the sibling handlers have.
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (!advertiserIds?.length) {
            throw new Error("Advertiser ids filter is required");
        }
        queryBody.filters["meta_inst.advertiser_id"] = advertiserIds.join(",");
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (!campaignIds?.length) {
            throw new Error("Campaign ids filter is required");
        }
        queryBody.filters["meta_inst.campaign_id"] = campaignIds.join(",");
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatResponse(data),
                queryMeta,
                // Read from the raw rows: formatResponse drops rows without an
                // image, so counting after it would under-report a full page.
                truncatedAt: data.length >= OVERVIEW_SCREENSHOTS_QUERY_LIMIT
                    ? OVERVIEW_SCREENSHOTS_QUERY_LIMIT
                    : undefined
            }
        ])
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch overview dashboard screenshots");
        }));
    }
    /**
     * Keyed by all three columns because Looker GROUP BYs that triple; any subset
     * collides. Keys off the raw values so trimming the publisher cannot merge
     * two distinct rows.
     */
    formatResponse(data) {
        return data
            .map((row) => {
            const publisher = String(row[OVERVIEW_SCREENSHOT_FIELDS.publisher] ?? "");
            const imageUrl = String(row[OVERVIEW_SCREENSHOT_FIELDS.imageUrl] ?? "");
            const capturedAt = String(row[OVERVIEW_SCREENSHOT_FIELDS.capturedAt] ?? "");
            return {
                // A small share of captures carry the publisher with stray leading or
                // trailing slashes ("/people.com/"), which the tile would render
                // verbatim next to correctly formed neighbours.
                publisher: publisher.replace(/^\/+|\/+$/g, ""),
                imageUrl,
                capturedAt,
                id: `${publisher}::${imageUrl}::${capturedAt}`
            };
        })
            .filter((row) => row.imageUrl);
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=Screenshots.js.map